import React, { useState } from 'react';
import {
  Container,
  Button,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  Box,
  Typography,
  LinearProgress,
  Card,
  CardContent,
  Stack,
} from '@mui/material';
import { useMutation } from 'react-query';
import { auth, downloads } from '../services/auth';
import toast from 'react-hot-toast';
import { motion } from 'framer-motion';
import UpgradeModal from '../components/UpgradeModal';

const MotionCard = motion(Card);

const Downloader: React.FC = () => {
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [operation, setOperation] = useState('transcribe');
  const [statusData, setStatusData] = useState<any>(null);
  const [downloadTask, setDownloadTask] = useState<any>(null);
  const [progress, setProgress] = useState(0);

  const [start, setStart] = useState(0);
  const [duration, setDuration] = useState(10);

  const [upgradeData, setUpgradeData] = useState<{
    reason?: string;
    cta?: string;
  }>({});

  const openUpgradeModal = () => setShowUpgrade(true);
  const closeUpgradeModal = () => setShowUpgrade(false);


  // Upload Mutation
  const uploadMutation = useMutation(
    () => {
      if (!file) throw new Error('No file selected');
      return downloads.uploadFile(file, operation, {
          start,
          duration,
        });
    },
    {
      onSuccess: (data) => {
        setDownloadTask(data);
        toast.success('Processing started!');
        pollStatus(data.task_id);
      },
      onError: (error: any) => {
        const detail = error.response?.data?.detail;

        if (typeof detail === 'object' && detail?.upgrade_required) {
          toast.error(detail.message || 'Upgrade required');
          setUpgradeData({
            reason: detail.reason,
            cta: detail.cta,
          });
          openUpgradeModal();
          return;
        }

        toast.error(detail || 'Upload failed');
      },
    }
  );

  // Poll status
  const pollStatus = (taskId: string) => {
    const interval = setInterval(async () => {
      try {
        const res = await downloads.getStatus(taskId);

        setProgress(res.progress || 0);
        setStatusData(res);

        if (res.status === 'completed') {
          setProgress(100);
          clearInterval(interval);
          toast.success('Processing completed!');
        if (res.download_url && downloadWindow) {

          downloadWindow.location.href = res.download_url;
        }
        }

        if (res.status === 'failed') {
          clearInterval(interval);
          toast.error(res.error_message || 'Failed');
        }
      } catch {
        clearInterval(interval);
        toast.error('Status error');
      }
    }, 2000);
  };

  let downloadWindow: Window | null = null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!file) {
      toast.error('Please upload a file');
      return;
    }
    // ✅ Open tab immediately (allowed)
    downloadWindow = window.open("", "_blank");

    uploadMutation.mutate();
  };

  const glassCard = {
    borderRadius: 3,
    backdropFilter: 'blur(14px)',
    background:
      'linear-gradient(135deg, rgba(255,255,255,0.75), rgba(255,255,255,0.4))',
    border: '1px solid rgba(255,255,255,0.2)',
    boxShadow: '0 10px 40px rgba(0,0,0,0.06)',
    transition: 'all 0.25s ease',
    '&:hover': {
      transform: 'translateY(-3px)',
      boxShadow: '0 20px 60px rgba(0,0,0,0.08)',
    },
  };

  return (
    <Container maxWidth="md">
      <Box sx={{ py: 4 }}>
        {/* HEADER */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" fontWeight={700}>
            🎬 AI Video Processor
          </Typography>
          <Typography color="text.secondary">
            Upload videos to transcribe, extract audio, or compress
          </Typography>
        </Box>

        {/* MAIN CARD */}
        <MotionCard initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} sx={glassCard}>
          <CardContent>
            <form onSubmit={handleSubmit}>
              <Stack spacing={3}>

                {/* FILE INPUT */}
                <Button variant="outlined" component="label">
                  {file ? file.name : 'Upload File'}
                  <input
                    type="file"
                    hidden
                    onChange={(e) => setFile(e.target.files?.[0] || null)}
                  />
                </Button>

                {/* OPERATION */}
                <Box>
                  <Typography fontWeight={600} mb={1}>
                    Operation
                  </Typography>
                  <FormControl>
                    <RadioGroup
                      row
                      value={operation}
                      onChange={(e) => setOperation(e.target.value)}
                    >
                      <FormControlLabel value="transcribe" control={<Radio />} label="Transcribe" />
                      <FormControlLabel value="mp3" control={<Radio />} label="Extract MP3" />
                      <FormControlLabel value="compress" control={<Radio />} label="Compress" />
                      <FormControlLabel value="trim" control={<Radio />} label="Trim Video" />
                    </RadioGroup>
                  </FormControl>
                </Box>

                {operation === "trim" && (
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  
                  <FormControl fullWidth>
                    <Typography variant="body2" fontWeight={600} mb={0.5}>
                      Start Time
                    </Typography>

                    <input
                      type="number"
                      min={0}
                      value={start}
                      onChange={(e) => setStart(Number(e.target.value))}
                      style={{
                        padding: "12px",
                        borderRadius: "10px",
                        border: "1px solid #ccc",
                        fontSize: "16px",
                      }}
                    />

                    <Typography variant="caption" color="text.secondary">
                      Enter start time in seconds
                    </Typography>
                  </FormControl>

                  <FormControl fullWidth>
                    <Typography variant="body2" fontWeight={600} mb={0.5}>
                      Duration
                    </Typography>

                    <input
                      type="number"
                      min={1}
                      value={duration}
                      onChange={(e) => setDuration(Number(e.target.value))}
                      style={{
                        padding: "12px",
                        borderRadius: "10px",
                        border: "1px solid #ccc",
                        fontSize: "16px",
                      }}
                    />

                    <Typography variant="caption" color="text.secondary">
                      Enter clip length in seconds
                    </Typography>
                  </FormControl>

                </Stack>
              )}

                {/* SUBMIT */}
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  fullWidth
                  disabled={uploadMutation.isLoading}
                >
                  {uploadMutation.isLoading ? 'Starting...' : 'Start Processing'}
                </Button>

                {/* PROGRESS */}
                {downloadTask && (
                  <Box
                    sx={{
                      p: 3,
                      borderRadius: 4,
                      background:
                        "linear-gradient(135deg, rgba(255,255,255,0.7), rgba(255,255,255,0.35))",
                      border: "1px solid rgba(255,255,255,0.2)",
                      backdropFilter: "blur(12px)",
                      boxShadow: "0 8px 32px rgba(0,0,0,0.08)",
                    }}
                  >
                    {/* HEADER */}
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={1.5}
                    >
                      <Typography fontWeight={700}>
                        {statusData?.status === "completed"
                          ? "✅ Download Completed"
                          : statusData?.status === "failed"
                          ? "❌ Processing Failed"
                          : "⚙️ Processing File"}
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{
                          px: 1.5,
                          py: 0.5,
                          borderRadius: 20,
                          background:
                            statusData?.status === "completed"
                              ? "rgba(76,175,80,0.15)"
                              : "rgba(25,118,210,0.12)",
                          color:
                            statusData?.status === "completed"
                              ? "success.main"
                              : "primary.main",
                          fontWeight: 600,
                        }}
                      >
                        {Math.round(progress)}%
                      </Typography>
                    </Stack>

                    {/* PROGRESS BAR */}
                    <LinearProgress
                      variant="determinate"
                      value={progress}
                      sx={{
                        height: 10,
                        borderRadius: 999,
                        backgroundColor: "rgba(0,0,0,0.05)",
                        "& .MuiLinearProgress-bar": {
                          borderRadius: 999,
                        },
                      }}
                    />

                    {/* STATUS TEXT */}
                    <Stack spacing={0.5} mt={2}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                          color:
                            statusData?.status === "completed"
                              ? "success.main"
                              : "text.primary",
                        }}
                      >
                        {statusData?.status === "completed" &&
                          "🎉 Your file is ready for download"}

                        {statusData?.status === "failed" &&
                          (statusData?.error_message || "Processing failed")}

                        {statusData?.status !== "completed" &&
                          statusData?.status !== "failed" &&
                          progress < 20 &&
                          "🔄 Uploading & initializing..."}

                        {statusData?.status !== "completed" &&
                          statusData?.status !== "failed" &&
                          progress >= 20 &&
                          progress < 70 &&
                          "⚙️ Processing media..."}

                        {statusData?.status !== "completed" &&
                          statusData?.status !== "failed" &&
                          progress >= 70 &&
                          "📦 Finalizing output..."}
                      </Typography>

                      {!auth.getCurrentUser()?.is_premium ? (
                        <Typography
                          variant="caption"
                          sx={{
                            color: "text.secondary",
                          }}
                        >
                          ⚡ Free processing speed
                        </Typography>
                      ) : (
                        <Typography
                          variant="caption"
                          sx={{
                            color: "success.main",
                            fontWeight: 600,
                          }}
                        >
                          🚀 Premium acceleration enabled
                        </Typography>
                      )}
                    </Stack>
                  </Box>
                )}

              </Stack>
            </form>
          </CardContent>
        </MotionCard>

        {/* TRANSCRIPT */}
        {statusData?.transcript && (
          <MotionCard sx={{ ...glassCard, mt: 3 }}>
            <CardContent>
              <Typography variant="h6">📝 Transcript</Typography>

              <Box sx={{ maxHeight: 200, overflow: 'auto', p: 2, background: '#f5f5f5', borderRadius: 2 }}>
                <Typography variant="body2">{statusData.transcript}</Typography>
              </Box>

              <Box mt={2}>
                <Button
                  variant="contained"
                  onClick={() => window.open(statusData.download_url, "_blank")}
                     
                >
                  ⬇ Download File
                </Button>
              </Box>
            </CardContent>
          </MotionCard>
        )}

        {/* SUMMARY */}
        {statusData?.summary && (
          <MotionCard sx={{ ...glassCard, mt: 3 }}>
            <CardContent>
              <Typography variant="h6">🧠 Summary</Typography>
              <Typography>{statusData.summary}</Typography>
            </CardContent>
          </MotionCard>
        )}

        {/* LANGUAGE */}
        {statusData?.language && (
          <Typography mt={2}>
            🌍 Language: {statusData.language}
          </Typography>
        )}
      </Box>

      <UpgradeModal
        isOpen={showUpgrade}
        onClose={closeUpgradeModal}
        reason={upgradeData.reason}
        cta={upgradeData.cta}
      />
    </Container>
  );
};

export default Downloader;